import {
  MAX_POLL_OPTIONS_COUNT,
  NEW_POLL_OPTION,
} from "@circle-react-shared/uikit/TipTapBlockEditor/PollExtension/PollModal/constants";
import { Button } from "@circleco/compass/components/Button";
import { IconButton } from "@circleco/compass/components/IconButton";
import {
  DragHandle,
  SortableList,
} from "@circleco/compass/components/SortableList";
import { TextInput } from "@circleco/compass/components/TextInput";
import { Typography } from "@circleco/compass/components/Typography";
import type { ReactElement } from "react";
import { useEffect, useMemo, useRef } from "react";
import {
  Controller,
  useFieldArray,
  useFormContext,
  useFormState,
} from "react-hook-form";

import { t } from "@/i18n-js/instance";

interface PollOptionSortableItem {
  fieldId: string;
  id: string;
}

interface PollOptionsProps {
  disabled?: boolean;
}

export const PollOptions = ({ disabled }: PollOptionsProps) => {
  const { control } = useFormContext();
  const { fields, insert, remove, move } = useFieldArray({
    control,
    name: "poll_options",
  });
  const { errors } = useFormState({ name: "poll_options" });
  const pendingFocusIndexRef = useRef<number | null>(null);
  const inputRefs = useRef<Map<number, HTMLInputElement>>(new Map());

  const sortableItems = useMemo<PollOptionSortableItem[]>(
    () =>
      fields.map(field => ({
        id: field.id,
        fieldId: field.id,
      })),
    [fields],
  );

  useEffect(() => {
    const focusIndex = pendingFocusIndexRef.current;
    if (focusIndex === null) return;
    pendingFocusIndexRef.current = null;
    inputRefs.current.get(focusIndex)?.focus();
  }, [fields]);

  const handleItemsChange = (items: PollOptionSortableItem[]) => {
    let order = fields.map(field => field.id);
    const targetOrder = items.map(item => item.fieldId);

    if (order.join() === targetOrder.join()) return;

    for (let index = 0; index < targetOrder.length; index += 1) {
      const fieldId = targetOrder[index];
      const currentIndex = order.indexOf(fieldId);
      if (currentIndex === -1 || currentIndex === index) continue;

      move(currentIndex, index);
      const nextOrder = [...order];
      const [movedId] = nextOrder.splice(currentIndex, 1);
      nextOrder.splice(index, 0, movedId);
      order = nextOrder;
    }
  };

  const renderItem = (item: PollOptionSortableItem): ReactElement => {
    const index = fields.findIndex(field => field.id === item.fieldId);
    if (index < 0) {
      return <div />;
    }

    return (
      <div className="flex items-start gap-2">
        {!disabled && (
          <DragHandle
            render={({ ref, style }) => (
              <IconButton
                ref={ref}
                style={style}
                aria-label={t("tiptap.extensions.poll.options")}
                className="mt-1 cursor-grab"
                icon="drag"
                size="sm"
                type="button"
                variant="ghost"
              />
            )}
          />
        )}
        <div className="min-w-0 flex-1">
          <Controller
            control={control}
            name={`poll_options.${index}.value`}
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                ref={element => {
                  field.ref(element);
                  if (element) {
                    inputRefs.current.set(index, element);
                  } else {
                    inputRefs.current.delete(index);
                  }
                }}
                disabled={disabled}
                error={fieldState.error?.message}
                id={`poll_options-${index}`}
                onKeyDown={event => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    if (fields.length >= MAX_POLL_OPTIONS_COUNT) return;
                    pendingFocusIndexRef.current = index + 1;
                    insert(index + 1, NEW_POLL_OPTION);
                    return;
                  }

                  if (
                    event.key === "Backspace" &&
                    !field.value &&
                    fields.length > 1
                  ) {
                    event.preventDefault();
                    pendingFocusIndexRef.current = Math.max(0, index - 1);
                    remove(index);
                  }
                }}
                placeholder={t("tiptap.extensions.poll.option_placeholder", {
                  number: index + 1,
                })}
                data-testid="poll-option-item"
              />
            )}
          />
        </div>
        {!disabled && fields.length > 1 && (
          <IconButton
            aria-label={t("close")}
            className="mt-1"
            icon="cross"
            onClick={() => remove(index)}
            size="sm"
            type="button"
            variant="ghost"
          />
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <Typography variant="label-md">
        {t("tiptap.extensions.poll.options")}
      </Typography>
      {disabled && (
        <Typography variant="body-sm" color="secondary">
          {t("tiptap.extensions.poll.cannot_edit_options")}
        </Typography>
      )}
      <SortableList<PollOptionSortableItem>
        axis="vertical"
        className="flex flex-col gap-2"
        draggingClassName="opacity-40"
        items={sortableItems}
        mode="flat"
        onItemsChange={handleItemsChange}
        renderItem={renderItem}
        renderAddNew={
          !disabled && fields.length < MAX_POLL_OPTIONS_COUNT
            ? () => (
                <Button
                  onClick={() => insert(fields.length, NEW_POLL_OPTION)}
                  size="sm"
                  startIcon="plus"
                  type="button"
                  variant="ghost"
                >
                  {t("tiptap.extensions.poll.add_option")}
                </Button>
              )
            : undefined
        }
        restrictToContainer
      />
      {typeof errors.poll_options?.message === "string" && (
        <Typography color="danger" variant="body-sm">
          {errors.poll_options.message}
        </Typography>
      )}
    </div>
  );
};
